import React, { useState } from "react"
import {
  Typography,
  Container,
  Box,
  AppBar,
  Toolbar,
  Button,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { ArrowBack } from "@mui/icons-material"

const ContactPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const navigate = useNavigate()
  const formRef = React.useRef<HTMLFormElement>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)

    const formData = new FormData(event.currentTarget)
    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      subject: formData.get("subject") as string,
      message: formData.get("message") as string,
    }

    try {
      const response = await fetch("https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        throw new Error("Failed to send message")
      }

      setSnackbarMessage("Thank you for your message. We'll get back to you soon!")
      setSnackbarSeverity("success")
      setSnackbarOpen(true)
      formRef.current?.reset()
    } catch (error) {
      console.log(error)
      setSnackbarMessage("Failed to send message. Please try again later.")
      setSnackbarSeverity("error")
      setSnackbarOpen(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "none",
        color: "rgba(226, 232, 240, 0.9)",
        display: "flex",
        flexDirection: "column",
        zIndex: 0,
      }}
    >
      {/* Sticky Header */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "rgba(10, 15, 21, 0)",
          boxShadow: "none",
          zIndex: 1,
        }}
      >
        <Toolbar>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/")}
            sx={{
              color: "rgba(226, 232, 240, 0.9)",
              "&:hover": {
                backgroundColor: "rgba(30, 41, 59, 0.5)",
              },
            }}
          >
            Back
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{
          pb: { xs: 4, md: 6 },
          px: { xs: 2, md: 3 },
          flexGrow: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#FFF",
                mb: 1,
              }}
            >
              Get in Touch
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(148, 163, 184, 0.9)" }}>
              We'd love to hear from you
            </Typography>
          </Box>

          {/* Contact Form Card */}
          <Card
            sx={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderColor: "rgba(30, 41, 59, 0.8)",
              borderRadius: 1,
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              transition: "box-shadow 0.2s",
              "&:hover": {
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              },
            }}
            variant="outlined"
          >
            <CardHeader
              title={
                <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                  Send us a message
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ mx: 3, backgroundColor: "rgba(71, 85, 105, 0.8)" }} />
            <CardContent sx={{ pt: 3 }}>
              <form ref={formRef} onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": { borderColor: "rgba(255,255,255,0.23)" },
                          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                          "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.7)",
                          "&.Mui-focused": { color: "rgba(255,255,255,0.9)" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      required
                      type="email"
                      variant="outlined"
                      disabled={isSubmitting}
                      inputProps={{
                        pattern: "[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$",
                        title: "Please enter a valid email address"
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": { borderColor: "rgba(255,255,255,0.23)" },
                          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                          "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.7)",
                          "&.Mui-focused": { color: "rgba(255,255,255,0.9)" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="subject"
                      label="Subject"
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": { borderColor: "rgba(255,255,255,0.23)" },
                          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                          "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.7)",
                          "&.Mui-focused": { color: "rgba(255,255,255,0.9)" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="message"
                      label="Message"
                      required
                      multiline
                      rows={4}
                      variant="outlined"
                      disabled={isSubmitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": { borderColor: "rgba(255,255,255,0.23)" },
                          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                          "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.7)",
                          "&.Mui-focused": { color: "rgba(255,255,255,0.9)" },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                      sx={{
                        mt: 1,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(123, 44, 191, 0.3)",
                        background: "linear-gradient(45deg, #7B2CBF, #E31B88)",
                        "&:hover": {
                            background: "linear-gradient(45deg, #9D4EDD, #FF3399)",
                            boxShadow: "0 6px 25px rgba(123, 44, 191, 0.4)",
                        },
                      }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Footer */}
      {!isMobile && (
        <Box
          component="footer"
          sx={{
            borderTop: "1px solid rgba(30, 41, 59, 0.8)",
            py: 3,
            mt: "auto",
          }}
        >
          <Container>
            <Typography variant="body2" align="center" sx={{ color: "rgba(148, 163, 184, 0.9)" }}>
              © {new Date().getFullYear()} Time Capsule. All rights reserved.
            </Typography>
          </Container>
        </Box>
      )}

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            backgroundColor: "rgba(15, 23, 42, 0.9)",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ContactPage
